// LAP-752 — Eco recommendation card, layout aligned with the financial
// recommendation card (MonPatrimoine/ConseilsCard.js) for visual
// consistency across the app: same sparkle header, score chip, "tip of
// the day · generated X ago" sub-header, italic body with a coloured left
// border, signature inside the border, and two action buttons (Nouvelle
// recommandation / Historique) above a primary CTA ("Activer").
//
// Eco-specific differences vs the finance card:
//   • Sparkle / left border / hover halo in green (eco palette #2fd28a),
//     not purple. Keeps the visual hierarchy identical but signals domain.
//   • The signature is "Generated by Claude" with an Anthropic-style
//     orange mark — the user explicitly asked Claude be surfaced.
//   • The credit cost displayed is 1 credit/regeneration (aligned on
//     finance LAP-CP02 pricing — single source of truth in cron.py
//     `base_cost=1`).
//   • The primary CTA at the bottom is "Activer cette recommandation"
//     which calls /activate-recommendation/<id>/ — eco-specific feature.
import { Box, Button, Card, Chip } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import HistoryIcon from '@mui/icons-material/History'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import relativeTime from 'dayjs/plugin/relativeTime'
import { isEmpty } from 'lodash'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import WTypography from '../../../../Components/whiteTypography'
import { wizardAPI } from '../../../../utils/wizardAPI'
// LAP-766 — Score écologique RÉEL (mêmes règles que la page Conseiller Écologie),
// pour remplacer le score naïf client par la note de l'analyse complète.
import { buildEcoAdvisor } from '../../../ConseillerEcologie/utils/buildEcoAdvisor'
import { fetchEcoAdvisorSources } from '../../../ConseillerEcologie/utils/fetchEcoAdvisorSources'

dayjs.extend(relativeTime)

const ECO_RECO_CREDIT_COST = 1 // LAP-752 — must match backend `base_cost`
                               // in Backendlife/cron.py.

// LAP-766 — Garde-fou anti-blocage : la régénération appelle Claude côté
// backend (appel synchrone potentiellement long) et axios n'a AUCUN timeout
// configuré (cf. utils/api.js). Sans borne, le bouton « Nouvelle
// recommandation » pouvait tourner indéfiniment sans jamais ni aboutir ni
// afficher d'erreur (symptôme « bouton sans effet / charge »). On borne donc
// l'attente côté client : passé ce délai, on rend la main et on affiche une
// erreur explicite au lieu de laisser le spinner tourner pour toujours.
const ECO_REGEN_TIMEOUT_MS = 45000

function withClientTimeout(promise, ms, timeoutError) {
  let timer
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(timeoutError), ms)
  })
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer))
}

// LAP-766 — Couleur du chip de score, alignée sur les conseillers (≥70 vert,
// ≥45 ambre, sinon rouge).
function scoreChipColors(score) {
  if (score == null) return { bg: 'rgba(148,163,184,0.18)', fg: '#94a3b8' }
  if (score >= 70) return { bg: 'rgba(74,222,128,0.18)', fg: '#4ade80' }
  if (score >= 45) return { bg: 'rgba(251,191,36,0.18)', fg: '#fbbf24' }
  return { bg: 'rgba(248,113,113,0.18)', fg: '#f87171' }
}

const ECO_PURPLE = '#9b8cff' // unused, kept for diff readability
const ECO_GREEN = '#2fd28a'
const CLAUDE_ORANGE = '#d97757'

// Try to parse the JSON payload Claude wrote into DisplayedAdvice.data.
// Older rows (OpenAI era) have data=null and only `content` as plain text.
function parseAdvicePayload(advice) {
  if (!advice || !advice.data) return null
  try {
    const parsed = typeof advice.data === 'string' ? JSON.parse(advice.data) : advice.data
    if (parsed && typeof parsed === 'object') return parsed
  } catch (_) {}
  return null
}

// LAP-752 — Naive eco score 0-100 derived from the structured payload.
// We give credit for (a) targeting a known category, (b) a reasonable
// reduction %, and (c) low effort. This stays client-side because we
// don't yet have a server-side eco score; the financial card uses the
// same teaser strategy (Chip is also static there in LAP-CP01).
function computeEcoScore(structured) {
  if (!structured) return null
  let score = 40
  if (structured.category) score += 15
  const pct = parseFloat(structured.reduction_percentage || 0)
  if (pct > 0) score += Math.min(25, Math.round(pct))
  if (structured.effort_level === 'low') score += 15
  else if (structured.effort_level === 'medium') score += 8
  return Math.max(0, Math.min(100, score))
}

function ConseilsCard({ oldData }) {
  const { t, i18n } = useTranslation('layout')
  const navigate = useNavigate()
  const [advice, setAdvice] = useState(oldData)
  const [regenerating, setRegenerating] = useState(false)
  const [activating, setActivating] = useState(false)
  const [toast, setToast] = useState(null) // { severity, msg }
  // LAP-752 v2 — `insufficientData` is true when /regenerate returned 422.
  // We surface a guided empty state (link to each questionnaire) in that
  // case rather than the generic toast.
  const [insufficientData, setInsufficientData] = useState(null) // null | { missing: [...] }

  // LAP-766 — Score écologique RÉEL (note 0-100 de l'analyse complète). On le
  // récupère une fois au montage : il s'appuie sur l'empreinte carbone
  // (logement/transport/alimentation/shopping) vs la moyenne nationale, comme
  // la page Conseiller Écologie. Tant qu'il n'est pas chargé, on retombe sur le
  // score naïf dérivé du payload Claude.
  const [realScore, setRealScore] = useState(null)

  // Keep local state in sync if the parent refetches /reports/.
  useEffect(() => {
    setAdvice(oldData)
  }, [oldData])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const sources = await fetchEcoAdvisorSources()
        const built = buildEcoAdvisor({ ...sources, t })
        if (!cancelled) setRealScore(built?.ecoScore?.global ?? null)
      } catch (_) {
        /* silencieux — on garde le score naïf en repli */
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const structured = useMemo(() => parseAdvicePayload(advice), [advice])
  const naiveScore = useMemo(() => computeEcoScore(structured), [structured])
  // Le score réel prime ; repli sur le score naïf si l'agrégat n'est pas encore
  // chargé (ou indisponible).
  const score = realScore != null ? realScore : naiveScore
  const reductionPct = advice?.reduction_percentage
  const alreadyActivated = !!advice?.objective_created
  const canActivate = !!reductionPct && parseFloat(reductionPct) > 0
  // LAP-752 v2 — Detect legacy (pre-Claude) advices. The backend marks them
  // with `legacy: true` in /reports/. If we got an advice but it has no
  // structured payload AND no reduction_percentage, the row was almost
  // certainly written by the old OpenAI cron whose prompt was loose enough
  // to occasionally produce off-topic content (e.g. financial advice
  // surfacing on the eco card). We prompt the user to regenerate.
  const isLegacy =
    !!advice &&
    !isEmpty(advice) &&
    (advice.legacy === true ||
      (advice.source !== 'Claude' && !structured && !reductionPct))

  // Relative date (dayjs.fromNow) — mirrors the finance card's sub-header.
  const lang = (i18n.language || 'fr').split('-')[0]
  const formatted = (() => {
    const d =
      advice?.created_at ||
      advice?.createdAt ||
      advice?.creation_date ||
      advice?.date
    if (!d) return null
    try {
      const dj = dayjs(d).locale(lang === 'fr' ? 'fr' : 'en')
      return { absolute: dj.format(lang === 'fr' ? 'D MMM · HH[h]mm' : 'MMM D · HH:mm'), relative: dj.fromNow() }
    } catch {
      return null
    }
  })()

  // ─── Handlers ───────────────────────────────────────────────────────
  const handleRegenerate = async (e) => {
    if (e) e.stopPropagation()
    setRegenerating(true)
    setInsufficientData(null)
    try {
      // LAP-766 — Borne l'attente : si l'appel Claude côté backend ne répond
      // pas dans le délai imparti, on rejette avec une erreur explicite plutôt
      // que de laisser le bouton tourner indéfiniment (bug « sans effet »).
      const fresh = await withClientTimeout(
        wizardAPI.regenerateEcoRecommendation(),
        ECO_REGEN_TIMEOUT_MS,
        {
          status: 0,
          timeout: true,
          message: t(
            'dashboard.regenerateTimeout',
            'La génération a pris trop de temps. Réessayez dans un instant.'
          ),
        }
      )
      setAdvice(fresh)
      setToast({
        severity: 'success',
        msg: t('dashboard.regenerateOk', 'Nouvelle recommandation générée.'),
      })
    } catch (err) {
      // LAP-752 v3 — Distinct error mapping (was lumping everything as 402
      // "Crédits insuffisants" which masked the real cause).
      if (err?.status === 422) {
        // Insufficient eco data → guided empty state inline (not a toast).
        setInsufficientData({
          missing: err?.data?.missing || ['logement', 'transport', 'alimentation', 'shopping'],
          detail: err?.data?.detail,
        })
      } else if (err?.status === 402) {
        setToast({
          severity: 'warning',
          msg:
            err?.data?.detail ||
            t(
              'dashboard.creditsLow',
              'Crédits insuffisants pour générer une nouvelle recommandation.'
            ),
        })
      } else if (err?.status === 503) {
        // AI provider down / config missing / Claude returned no tool call.
        // LAP-752 v4 — Le backend renvoie désormais la cause technique réelle
        // dans `error` (clé API absente, SDK manquant, import cron…). On la
        // trace en console ET on l'ajoute au toast pour un diagnostic immédiat
        // au lieu d'un message générique opaque.
        if (err?.data?.error) {
          // eslint-disable-next-line no-console
          console.error('[eco-reco] cause technique:', err.data.error)
        }
        setToast({
          severity: 'error',
          msg:
            (err?.data?.detail ||
              t(
                'dashboard.aiUnavailable',
                'Service IA temporairement indisponible. Réessayez dans un instant.'
              )) + (err?.data?.error ? ` (${err.data.error})` : ''),
        })
      } else {
        // LAP-752 v4 — Idem pour un éventuel 500 résiduel : on remonte la cause.
        if (err?.data?.error || err?.data?.detail) {
          // eslint-disable-next-line no-console
          console.error('[eco-reco] erreur:', err.data.error || err.data.detail)
        }
        setToast({
          severity: 'error',
          msg:
            err?.data?.error ||
            err?.data?.detail ||
            err?.message ||
            t('dashboard.regenerateError', 'Erreur, réessayez plus tard.'),
        })
      }
    } finally {
      setRegenerating(false)
    }
  }

  const handleActivate = async (e) => {
    if (e) e.stopPropagation()
    if (!advice?.id || activating || alreadyActivated) return
    setActivating(true)
    try {
      const result = await wizardAPI.activateEcoRecommendation(advice.id)
      // Update local state so the button switches to "✓ Objectif actif"
      // without waiting for the parent /reports/ refetch.
      setAdvice({ ...advice, objective_created: result.objective_id })
      setToast({
        severity: 'success',
        msg: t(
          'dashboard.activateSuccess',
          `Objectif créé : Réduire mon empreinte de ${result.reduction_percentage}% sur l'année`
        ),
      })
    } catch (err) {
      if (err?.status === 409) {
        setAdvice({ ...advice, objective_created: err?.data?.objective_id })
        setToast({
          severity: 'info',
          msg: t('dashboard.activateAlready', 'Cette recommandation est déjà active.'),
        })
      } else {
        setToast({
          severity: 'error',
          msg: err?.message || t('dashboard.activateError', 'Activation impossible'),
        })
      }
    } finally {
      setActivating(false)
    }
  }

  // ─── Empty state — no advice yet OR backend returned 422 ────────────
  if (isEmpty(advice) || insufficientData) {
    const missing =
      insufficientData?.missing || ['logement', 'transport', 'alimentation', 'shopping']
    return (
      <Card className="dashboard_card__root1" sx={cardBase}>
        <Box sx={headerRow}>
          <Box display="flex" alignItems="center" gap={1}>
            <AutoAwesomeIcon sx={{ color: ECO_GREEN, fontSize: 22 }} />
            <WTypography component="span" variant="h6">
              {t('dashboard.conseilsAstuces', 'Conseils & Astuces')}
            </WTypography>
          </Box>
        </Box>

        <Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center" mt={3} mb={2} px={1.5} textAlign="center">
          <WTypography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, mb: 1 }}>
            {insufficientData
              ? t(
                  'dashboard.recoInsufficientTitle',
                  'Renseignez vos données pour une recommandation personnalisée'
                )
              : t('dashboard.recoEmptyTitle', 'Pas encore de recommandation')}
          </WTypography>
          <WTypography
            sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, maxWidth: 360, mb: 2 }}
            variant="body2"
          >
            {insufficientData?.detail ||
              t(
                'dashboard.recoEmptyDesc',
                'L\'IA s\'appuie sur vos données pour vous donner un conseil chiffré et activable. Remplissez au moins une catégorie pour démarrer.'
              )}
          </WTypography>
          <Box display="flex" flexWrap="wrap" gap={0.75} justifyContent="center" className="no-drag">
            {missing.includes('logement') && (
              <Button
                size="small"
                onClick={() => navigate('/domicile/monimmobilier')}
                sx={emptyMissingCta}
              >
                🏠 {t('dashboard.catLogement', 'Logement')}
              </Button>
            )}
            {missing.includes('transport') && (
              <Button
                size="small"
                onClick={() => navigate('/domicile/mesvehicules')}
                sx={emptyMissingCta}
              >
                🚗 {t('dashboard.catTransport', 'Transport')}
              </Button>
            )}
            {missing.includes('alimentation') && (
              <Button
                size="small"
                onClick={() => navigate('/Dashboard?questionnaire=alimentation')}
                sx={emptyMissingCta}
              >
                🍽 {t('dashboard.catAlimentation', 'Alimentation')}
              </Button>
            )}
            {missing.includes('shopping') && (
              <Button
                size="small"
                onClick={() => navigate('/shopping')}
                sx={emptyMissingCta}
              >
                🛍 {t('dashboard.catShopping', 'Shopping')}
              </Button>
            )}
          </Box>
        </Box>

        {!insufficientData && (
          <Button
            fullWidth
            className="no-drag"
            onClick={handleRegenerate}
            disabled={regenerating}
            startIcon={
              <RestartAltIcon
                sx={{
                  animation: regenerating ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />
            }
            sx={{ ...btnSecondary, mb: 1 }}
          >
            {regenerating
              ? t('dashboard.regenerating', 'Génération…')
              : `${t('dashboard.newReco', 'Nouvelle recommandation')} (${ECO_RECO_CREDIT_COST} ${t('dashboard.creditAbbr', 'cr.')})`}
          </Button>
        )}

        {/* LAP-766 — Accès direct à l'analyse complète même sans recommandation :
            la page calcule le score et le détail à partir de l'empreinte. */}
        <Button
          fullWidth
          className="no-drag"
          onClick={() => navigate('/conseiller-ecologie')}
          startIcon={<OpenInFullIcon />}
          sx={btnGhost}
        >
          {t('dashboard.viewFullEcoAnalysis', "Voir l'analyse complète")}
        </Button>
      </Card>
    )
  }

  // ─── Active recommendation ──────────────────────────────────────────
  const title = structured?.title
  const body = structured?.content || advice?.content || ''
  const aiSource = (() => {
    const s = advice?.source
    if (!s) return t('dashboard.byClaude', 'Généré par Claude')
    // Old OpenAI rows: still surface them but rebrand the chip to Claude
    // when source matches legacy patterns — the data team will catch up.
    if (/(open\s*ai|openai|chat\s*gpt|chatgpt|gpt-?\d)/i.test(String(s))) {
      return t('dashboard.byClaude', 'Généré par Claude')
    }
    return s.toLowerCase() === 'claude' ? t('dashboard.byClaude', 'Généré par Claude') : s
  })()

  return (
    <Card className="dashboard_card__root1" sx={cardBase}>
      {/* ─── Header : sparkle + title + score chip ──────────────── */}
      <Box sx={headerRow}>
        <Box display="flex" alignItems="center" gap={1}>
          <AutoAwesomeIcon sx={{ color: ECO_GREEN, fontSize: 22 }} />
          <WTypography component="span" variant="h6">
            {t('dashboard.conseilsAstuces', 'Conseils & Astuces')}
          </WTypography>
        </Box>
        {score != null && (
          <Chip
            label={t('dashboard.scoreEco', { score, defaultValue: `Score ${score}/100` })}
            size="small"
            sx={{
              bgcolor: scoreChipColors(score).bg,
              color: scoreChipColors(score).fg,
              fontWeight: 700,
              height: 22,
            }}
          />
        )}
      </Box>

      {/* ─── Sub-header : "Conseil du jour · Généré il y a X" ───── */}
      <Box sx={{ ...headerRow, mt: 0.5 }}>
        <WTypography sx={{ color: '#ffffffa1' }} variant="body2">
          {t('dashboard.conseilDuJour', 'Conseil du jour')}
        </WTypography>
        {formatted?.relative && (
          <WTypography sx={{ color: '#ffffffa1', fontSize: 12 }} variant="body2">
            · {t('dashboard.generatedRelative', { when: formatted.relative, defaultValue: `Généré ${formatted.relative}` })}
          </WTypography>
        )}
      </Box>

      {/* ─── LAP-752 v2 — Legacy banner. Surfaced when the row is a pre-Claude
          OpenAI advice (no structured payload, no reduction_percentage, or
          backend tagged it with legacy=true). Prompts the user to regenerate
          so they get a quantified ecological reco — not the financial-looking
          text the old prompt sometimes produced. */}
      {isLegacy && (
        <Box
          className="no-drag"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mt: 1,
            mb: 1.5,
            px: 1.5,
            py: 1,
            borderRadius: 1,
            bgcolor: 'rgba(247,185,85,0.14)',
            border: '1px solid rgba(247,185,85,0.35)',
            color: '#ffe2b3',
            fontSize: 13,
          }}
        >
          <Box>
            <Box sx={{ fontWeight: 700, mb: 0.3 }}>
              {t('dashboard.legacyTitle', 'Recommandation obsolète')}
            </Box>
            <Box sx={{ fontSize: 12, opacity: 0.85 }}>
              {t(
                'dashboard.legacyDesc',
                'Cette recommandation a été générée par une ancienne version de l\'IA. Régénérez pour un conseil écologique chiffré et basé sur vos données.'
              )}
            </Box>
          </Box>
          <Button
            size="small"
            onClick={handleRegenerate}
            disabled={regenerating}
            startIcon={<RestartAltIcon />}
            sx={{
              textTransform: 'none',
              color: '#fff3dd',
              bgcolor: 'rgba(247,185,85,0.30)',
              border: '1px solid rgba(247,185,85,0.55)',
              fontWeight: 600,
              fontSize: 12,
              ml: 1,
              flexShrink: 0,
              '&:hover': { bgcolor: 'rgba(247,185,85,0.45)' },
            }}
          >
            {regenerating
              ? t('dashboard.regenerating', 'Génération…')
              : `${t('dashboard.regenerate', 'Régénérer')} (${ECO_RECO_CREDIT_COST} cr.)`}
          </Button>
        </Box>
      )}

      {/* ─── Body : italic left-border block with title + body + signature ─── */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          mt: 2,
          mb: 1.5,
          borderLeft: `3px solid ${ECO_GREEN}`,
          pl: 2.5,
          pr: 1,
          py: 1,
          color: 'white',
          fontStyle: 'italic',
          lineHeight: 1.65,
          fontSize: 15,
          // LAP-752 — long AI advice used to overflow the fixed-height grid
          // card and overlap the action buttons below (worse on mobile, where
          // the narrower card wraps text into more lines). Scroll the advice
          // inside its own region instead.
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(47,210,138,0.35)',
            borderRadius: '3px',
          },
        }}
      >
        {title && (
          <Box sx={{ fontWeight: 700, fontStyle: 'normal', fontSize: 16, mb: 0.5 }}>
            {title}
          </Box>
        )}
        <Box>{body}</Box>

        {/* "Generated by Claude" signature — Anthropic orange */}
        <Box
          mt={1.5}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.75,
            color: '#ffcdb8',
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: 0.3,
            fontStyle: 'normal',
          }}
        >
          <Box sx={claudeMarkStyle}>
            <svg viewBox="0 0 24 24" width="11" height="11" fill="#fff">
              <path d="M5.5 19l4.2-9.2h.2L14.1 19h2.3L11.2 5h-2L4 19h1.5zm9.4 0h2L11.2 5h-.4l3.7 14z" />
            </svg>
          </Box>
          {aiSource}
        </Box>
      </Box>

      {/* ─── LAP-752 fix — restored: `function` / `function_link` direct
          shortcut, used during onboarding to send the user to the matching
          questionnaire (e.g. "Compléter le questionnaire alimentation").
          Persisted on DisplayedAdvice by the seed ConseilTable rows.
          Rendered as an inline pill above the action buttons so it stays
          visible during onboarding even when the AI body is generic. */}
      {advice?.function && advice?.function_link && (
        <Box
          className="no-drag"
          onClick={() => navigate(advice.function_link)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 1.5,
            px: 1.5,
            py: 1,
            borderRadius: 1.5,
            cursor: 'pointer',
            bgcolor: 'rgba(67,130,149,0.18)',
            border: '1px solid rgba(67,130,149,0.45)',
            color: '#cfeff5',
            transition: 'all .2s',
            '&:hover': {
              bgcolor: 'rgba(67,130,149,0.30)',
              borderColor: '#438295',
            },
          }}
        >
          <WTypography sx={{ color: '#cfeff5', fontWeight: 600 }} variant="body2">
            {advice.function}
          </WTypography>
          <KeyboardArrowRightIcon
            sx={{ bgcolor: '#438295', borderRadius: '50%', color: 'white', fontSize: 22 }}
          />
        </Box>
      )}

      {/* ─── Action buttons : Nouvelle reco + Historique (cost chip) ──── */}
      <Box className="no-drag" sx={{ display: 'flex', gap: 1, mx: 0, mb: 1.5 }}>
        <Button
          fullWidth
          onClick={handleRegenerate}
          disabled={regenerating}
          startIcon={
            <RestartAltIcon
              sx={{
                animation: regenerating ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
          }
          sx={btnSecondary}
        >
          {regenerating
            ? t('dashboard.regenerating', 'Génération…')
            : `${t('dashboard.newReco', 'Nouvelle recommandation')} (${ECO_RECO_CREDIT_COST} ${t('dashboard.creditAbbr', 'cr.')})`}
        </Button>
        <Button
          fullWidth
          onClick={() => navigate('/Dashboard?tab=history')}
          startIcon={<HistoryIcon />}
          sx={btnGhost}
        >
          {t('dashboard.viewHistory', 'Historique')}
        </Button>
      </Box>

      {/* LAP-766 — Accès à l'analyse écologique complète (score, détail par
          poste, diagnostic, recommandations) — miroir des conseillers
          patrimonial / temps. */}
      <Button
        fullWidth
        className="no-drag"
        onClick={() => navigate('/conseiller-ecologie')}
        startIcon={<OpenInFullIcon />}
        sx={{ ...btnSecondary, mb: 1.5 }}
      >
        {t('dashboard.viewFullEcoAnalysis', "Voir l'analyse complète")}
      </Button>

      {/* ─── Primary CTA : Activer cette recommandation ──────────────── */}
      <Box
        className="no-drag"
        onClick={canActivate ? handleActivate : undefined}
        sx={{
          py: 1,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          bgcolor: alreadyActivated
            ? 'rgba(47,210,138,0.18)'
            : canActivate
              ? 'rgba(47,210,138,0.22)'
              : 'rgba(255,255,255,0.04)',
          border: `1px solid ${alreadyActivated ? 'rgba(47,210,138,0.5)' : canActivate ? 'rgba(47,210,138,0.45)' : 'rgba(255,255,255,0.12)'}`,
          borderRadius: 1.5,
          cursor: canActivate && !alreadyActivated && !activating ? 'pointer' : 'default',
          transition: 'all .2s',
          '&:hover': canActivate && !alreadyActivated
            ? { bgcolor: 'rgba(47,210,138,0.30)', borderColor: ECO_GREEN }
            : {},
        }}
      >
        {alreadyActivated ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon sx={{ color: ECO_GREEN, fontSize: 18 }} />
              <WTypography sx={{ color: '#b6f0d4', fontWeight: 600 }} variant="body2">
                {t('dashboard.objectiveCreatedShort', 'Objectif actif sur votre tableau de bord')}
              </WTypography>
            </Box>
            <ArrowOutwardIcon
              sx={{ color: '#b6f0d4', fontSize: 18, cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation()
                navigate('/mesobjectifs')
              }}
            />
          </>
        ) : (
          <>
            <WTypography
              sx={{ color: canActivate ? '#b6f0d4' : 'rgba(255,255,255,0.5)', fontWeight: 600 }}
              variant="body2"
            >
              {canActivate
                ? t('dashboard.activateCta', {
                    pct: Math.round(parseFloat(reductionPct) || 0),
                    defaultValue: `Activer (Réduire de ${Math.round(parseFloat(reductionPct) || 0)}% / an)`,
                  })
                : t('dashboard.activateUnavailable', 'Recommandation sans objectif chiffré')}
            </WTypography>
            <ArrowOutwardIcon
              sx={{ color: canActivate ? '#b6f0d4' : 'rgba(255,255,255,0.4)', fontSize: 18 }}
            />
          </>
        )}
      </Box>

      {/* Inline toast — kept minimal to avoid pulling in extra deps */}
      {toast && (
        <Box
          onClick={() => setToast(null)}
          sx={{
            mt: 1,
            px: 1.5,
            py: 1,
            borderRadius: 1,
            fontSize: 12,
            cursor: 'pointer',
            color: 'white',
            bgcolor:
              toast.severity === 'success'
                ? 'rgba(47,210,138,0.18)'
                : toast.severity === 'warning'
                  ? 'rgba(247,185,85,0.18)'
                  : toast.severity === 'info'
                    ? 'rgba(122,166,255,0.18)'
                    : 'rgba(255,91,107,0.18)',
            border: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          {toast.msg}
        </Box>
      )}
    </Card>
  )
}

// ─── Inline styles ────────────────────────────────────────────────────
const cardBase = {
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid rgba(47,210,138,0.20)',
  transition: 'transform .25s ease, box-shadow .25s ease, border-color .25s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(47,210,138,0.20)',
    borderColor: 'rgba(47,210,138,0.5)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: -60,
    right: -60,
    width: 180,
    height: 180,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(47,210,138,0.18) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
}
const headerRow = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  position: 'relative',
  zIndex: 1,
}
const claudeMarkStyle = {
  width: 16,
  height: 16,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 4,
  background: CLAUDE_ORANGE,
  boxShadow: '0 0 8px rgba(217,119,87,0.4)',
}
const btnSecondary = {
  bgcolor: 'rgba(47,210,138,0.18)',
  color: '#b6f0d4',
  fontWeight: 600,
  textTransform: 'none',
  border: '1px solid rgba(47,210,138,0.4)',
  py: 0.75,
  '&:hover': { bgcolor: 'rgba(47,210,138,0.30)', borderColor: ECO_GREEN },
  '&.Mui-disabled': { color: 'rgba(182,240,212,0.5)', borderColor: 'rgba(47,210,138,0.2)' },
}
const btnGhost = {
  color: '#dbe5f7',
  fontWeight: 600,
  textTransform: 'none',
  border: '1px solid rgba(255,255,255,0.18)',
  py: 0.75,
  '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.3)' },
}
const emptyMissingCta = {
  textTransform: 'none',
  color: '#b6f0d4',
  background: 'rgba(47,210,138,0.14)',
  border: '1px solid rgba(47,210,138,0.4)',
  fontSize: 12,
  fontWeight: 600,
  px: 1.5,
  py: 0.5,
  '&:hover': { background: 'rgba(47,210,138,0.26)', borderColor: '#2fd28a' },
}

export default ConseilsCard
